import jsPDF from "jspdf";

// A printable booklet of door signs — two per A4 page (cut along the middle
// with a guillotine). Each sign: brand logo, room name, house, and occupants,
// inside a bordered box.

const BRAND_NAVY = [26, 31, 113];
const LOGO = process.env.PUBLIC_URL + "/assets/img/blissLogo.png";

function roomLabel(room) {
  return room.groupName || room.bedroomName || room.roomName || "Room";
}

function occupantName(r) {
  return `${r.name || ""} ${r.surname || ""}`.trim() || "—";
}

function drawSign(doc, box, room) {
  const { x, y, w, h } = box;
  const cx = x + w / 2;

  // Border
  doc.setDrawColor(...BRAND_NAVY);
  doc.setLineWidth(1.5);
  doc.roundedRect(x, y, w, h, 10, 10);

  let cursor = y + 28;

  // Logo (centred). Guard so a load failure doesn't abort the whole booklet.
  const logoW = 44;
  const logoH = 53;
  try {
    doc.addImage(LOGO, "PNG", cx - logoW / 2, cursor, logoW, logoH);
  } catch (e) {
    /* logo unavailable — skip it rather than failing the export */
  }
  cursor += logoH + 30;

  // Room name (prominent)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(30);
  doc.setTextColor(...BRAND_NAVY);
  doc.text(String(roomLabel(room)), cx, cursor, { align: "center" });
  cursor += 26;

  // House
  if (room.house) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(15);
    doc.setTextColor(90);
    doc.text(String(room.house), cx, cursor, { align: "center" });
    cursor += 22;
  }

  // Divider
  doc.setDrawColor(...BRAND_NAVY);
  doc.setLineWidth(0.75);
  doc.line(x + 40, cursor, x + w - 40, cursor);
  cursor += 30;

  // Occupants — scale the font down a touch for busy rooms so names always fit.
  const residents = room.residents || [];
  const fontSize = residents.length > 8 ? 13 : 16;
  const lineGap = fontSize + 7;
  doc.setFont("helvetica", "normal");
  doc.setTextColor(40);
  doc.setFontSize(fontSize);
  residents.forEach((r) => {
    doc.text(occupantName(r), cx, cursor, { align: "center" });
    cursor += lineGap;
  });
}

export function generateRoomSignsPDF(
  displayState,
  formattedDate,
  selectedCampus,
  showToast
) {
  try {
    const rooms = (displayState?.rooms || [])
      .filter((room) => room.groupName && (room.residents?.length || 0) > 0)
      .sort((a, b) => {
        const ah = String(a.house || "");
        const bh = String(b.house || "");
        if (ah !== bh) return ah.localeCompare(bh, undefined, { numeric: true });
        return String(roomLabel(a)).localeCompare(
          String(roomLabel(b)),
          undefined,
          { numeric: true }
        );
      });

    if (rooms.length === 0) {
      showToast?.("No occupied rooms to print signs for.", "warning");
      return;
    }

    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const margin = 28;
    const gap = 24;
    const boxW = pageWidth - margin * 2;
    const boxH = (pageHeight - margin * 2 - gap) / 2;

    const slots = [
      { x: margin, y: margin, w: boxW, h: boxH },
      { x: margin, y: margin + boxH + gap, w: boxW, h: boxH },
    ];

    rooms.forEach((room, i) => {
      const slot = i % 2;
      if (i > 0 && slot === 0) doc.addPage();
      drawSign(doc, slots[slot], room);
    });

    doc.save(`room_signs_${formattedDate}_${selectedCampus || "campus"}.pdf`);
    showToast?.("Room signs PDF downloaded");
  } catch (err) {
    console.error("Room signs PDF export failed:", err);
    showToast?.("Room signs PDF export failed", "error");
  }
}
